import { problemDetails, type ProblemDetails } from '@solote/contracts';
import { apiBaseUrl, usesMocks } from '@/core/config/env';

/**
 * CLIENTE HTTP
 *
 * Fino de proposito. Toda a inteligencia de erro esta em traduzir o
 * ProblemDetails (RFC 7807) que os servicos devolvem — o mesmo formato vem da
 * API C# e do gateway, entao a UI trata erro de um jeito so.
 */

const BASE_URL = apiBaseUrl;

/**
 * A frase que a pessoa vai ler quando algo falhar.
 *
 * `detail` VEM ANTES DE `title`, e a ordem e o ponto.
 *
 * Os tres servicos montam o ProblemDetails do mesmo jeito: `title` e a
 * CATEGORIA — uma constante, "Nao foi possivel concluir" ou "Nao encontrado" —
 * e `detail` e a unica parte que diz o que houve: "Explique o motivo em pelo
 * menos 10 caracteres", "Nao ha o que aprovar em Removed", "Plano
 * indisponivel".
 *
 * Lendo `title` primeiro, o app inteiro respondia a mesma frase vazia para
 * TODO erro de negocio. A pessoa via "Nao foi possivel concluir" e nao tinha
 * como saber o que corrigir — a explicacao chegava no corpo da resposta e era
 * descartada aqui, a uma linha de distancia da tela.
 *
 * Em desenvolvimento com os mocks desligados, um 5xx quase sempre significa
 * "o backend nao esta de pe" — o proxy do Vite devolve 500 quando nao alcanca
 * o alvo. A mensagem crua manda a pessoa procurar um bug que nao existe,
 * entao dizemos o que de fato houve.
 */
function describeFailure(status: number, problem: ProblemDetails | null): string {
  const detalhe = problem?.detail?.trim();
  if (detalhe) return detalhe;

  if (problem?.title) return problem.title;

  if (import.meta.env.DEV && !usesMocks && status >= 500) {
    return (
      `Nao foi possivel falar com a API em ${BASE_URL} (${status}). ` +
      'O backend esta rodando? Para trabalhar sem ele, remova VITE_USE_MOCKS=false de apps/web/.env.'
    );
  }

  return `Falha na requisicao (${status}).`;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly problem: ProblemDetails | null;

  public constructor(status: number, problem: ProblemDetails | null, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** Erros de validacao por campo, prontos para o react-hook-form. */
  public get fieldErrors(): Record<string, string> {
    const errors = this.problem?.errors;
    if (!errors) return {};
    return Object.fromEntries(
      Object.entries(errors).map(([field, messages]) => [field, messages[0] ?? 'Valor invalido.']),
    );
  }

  /** Falhas transitorias merecem nova tentativa; 4xx de negocio nao. */
  public get isRetryable(): boolean {
    return this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

type TokenReader = () => string | null;

let readAccessToken: TokenReader = () => null;

/** O AuthProvider injeta o leitor de token para evitar dependencia circular. */
export function setTokenReader(reader: TokenReader): void {
  readAccessToken = reader;
}

/**
 * Token atual, para quem precisa montar a requisicao a mao.
 *
 * Serve a quem monta a requisicao a mao — hoje so o controle de galeria do
 * anunciante, que acompanha o progresso de cada arquivo. Sem este acesso, o
 * componente teria que ler o token de algum lugar global, e a unica opcao seria
 * guarda-lo onde um XSS o alcança.
 */
export function currentAccessToken(): string | null {
  return readAccessToken();
}

export type RequestOptions = {
  /**
   * Base alternativa para esta chamada. Serve para apontar um recurso
   * especifico a um servico ja integrado — hoje, o de pagamentos — enquanto o
   * resto continua nos mocks.
   */
  baseUrl?: string | null;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /**
   * Chave de idempotencia. Obrigatoria em qualquer operacao que gere cobranca:
   * o usuario que clica duas vezes em "Pagar" nao pode pagar duas vezes.
   */
  idempotencyKey?: string;
  headers?: Record<string, string>;
};

function buildUrl(path: string, query?: RequestOptions['query'], baseUrl?: string | null): string {
  const base = baseUrl ?? BASE_URL;
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    params.append(key, String(value));
  }
  const serialized = params.toString();
  return serialized ? `${url}?${serialized}` : url;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, idempotencyKey, headers = {}, baseUrl } = options;

  const token = readAccessToken();

  /*
    `FormData` PASSA DIRETO, sem serializar e sem `Content-Type`.

    O cabeçalho é omitido de propósito: o navegador precisa escrevê-lo sozinho
    para incluir a fronteira do multipart. Declará-lo à mão produz um corpo que
    o servidor não consegue separar — e o erro que volta fala de formato, não de
    cabeçalho, então a caçada começa no lugar errado.

    Antes disso, quem enviava arquivo montava o `fetch` na mão e perdia junto o
    tratamento de erro, a base alternativa e o cabeçalho de autorização.
  */
  const ehFormulario = typeof FormData !== 'undefined' && body instanceof FormData;

  const response = await fetch(buildUrl(path, query, baseUrl), {
    method,
    signal,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined && !ehFormulario ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      ...headers,
    },
    body: body === undefined ? undefined : ehFormulario ? (body as FormData) : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? safeParse(text) : null;

  if (!response.ok) {
    const parsed = problemDetails.safeParse(payload);
    const problem = parsed.success ? parsed.data : null;
    throw new ApiError(response.status, problem, describeFailure(response.status, problem));
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Chave de idempotencia estavel por tentativa de checkout. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}
